/**
 * Norse Quiz — animated FAQ accordion.
 *
 * A progressive enhancement over `<details>` / `<summary>`: with this script blocked
 * the rows still open and close, still respond to Enter and Space, and the `name`
 * attribute still keeps the group exclusive. All this adds is the movement.
 *
 * Why it needs script at all: a `<details>` stops rendering its content the moment
 * `open` is removed, so there is nothing left on screen to animate closed. The panel
 * has to be animated first and `open` removed afterwards. For the same reason the
 * native `name` grouping is dropped on upgrade — it snaps the outgoing row shut with
 * no animation — and exclusivity is handled here instead, so the row that closes
 * animates too.
 */
if (!customElements.get('norse-quiz-faq')) {
  const OPEN_MS = 260;
  const CLOSE_MS = 220;
  /* Matches the chevron's transition in norse-quiz-faq.css — change both together. */
  const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

  customElements.define(
    'norse-quiz-faq',
    class NorseQuizFaq extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('details'));

        this.items.forEach((item) => {
          // JS owns exclusivity from here, so the outgoing row can animate.
          item.removeAttribute('name');

          const summary = item.querySelector('summary');
          if (!summary) return;

          // A summary fires `click` for Enter and Space too, so one listener covers
          // pointer and keyboard.
          summary.addEventListener('click', (event) => {
            if (!this.panel(item)) return;
            event.preventDefault();
            this.toggle(item);
          });
        });
      }

      panel(item) {
        return item.querySelector('.nqf__answer');
      }

      get reduced() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      toggle(item) {
        if (item.open && !item.dataset.nqfClosing) this.collapse(item);
        else this.expand(item);
      }

      expand(item) {
        this.items.forEach((other) => {
          if (other !== item && other.open) this.collapse(other);
        });

        const panel = this.panel(item);
        this.cancel(item);
        delete item.dataset.nqfClosing;
        item.classList.remove('nqf__item--closing');
        item.open = true;

        if (this.reduced) return;

        // Measurable only once `open` is set, since until then it is not rendered.
        const target = panel.getBoundingClientRect().height;
        this.run(item, panel, 0, target, OPEN_MS).finished
          .then(() => {
            panel.style.height = '';
          })
          .catch(() => {});
      }

      collapse(item) {
        const panel = this.panel(item);

        if (this.reduced) {
          item.open = false;
          return;
        }

        const from = panel.getBoundingClientRect().height;
        this.cancel(item);

        // Flag and class go on immediately so a second click reads the row as closing
        // and the chevron starts turning back with the panel, not after it.
        item.dataset.nqfClosing = 'true';
        item.classList.add('nqf__item--closing');

        this.run(item, panel, from, 0, CLOSE_MS).finished
          .then(() => {
            item.open = false;
            panel.style.height = '';
            delete item.dataset.nqfClosing;
            item.classList.remove('nqf__item--closing');
          })
          .catch(() => {});
      }

      /** Cancels an in-flight animation so fast clicks do not fight each other. */
      cancel(item) {
        if (item._nqfAnimation) {
          item._nqfAnimation.cancel();
          item._nqfAnimation = null;
        }
      }

      /**
       * The panel's bottom padding is animated alongside its height. With
       * `box-sizing: border-box` a `height` of 0 still leaves the padding standing, so
       * height alone starts the move 24px tall on desktop — a visible jump at the top of
       * the open and the tail of the close. Taking the padding down with it starts at 0.
       */
      run(item, panel, from, to, duration) {
        const pad = parseFloat(getComputedStyle(panel).paddingBottom) || 0;
        const scale = (value) => (to || from ? (value / Math.max(from, to)) * pad : 0);
        const animation = panel.animate(
          {
            height: [`${from}px`, `${to}px`],
            paddingBottom: [`${scale(from)}px`, `${scale(to)}px`],
            opacity: [from ? 1 : 0, to ? 1 : 0],
          },
          { duration, easing: EASING }
        );
        item._nqfAnimation = animation;
        return animation;
      }
    }
  );
}
